import { createSlice } from '@reduxjs/toolkit';
import {
  addFollowers,
  changeObligationReporter,
  followSubmission,
  getReporters,
  getSubmission,
  sendMessage,
  unfollowSubmission,
  uploadFileDetailView,
} from './thunks';
import {
  flattenFileStructure,
  setSequenceNumbers,
} from 'utils/fileStructureUtils';

const initialState = {
  submissionDetail: {},
  submissionDetailLoading: false,
  submissionDetailError: false,
  fileUploaderOpen: false,
  fileUploading: false,
  addFollowersModalOpen: false,
  submissionFollowPending: false,
  submissionUnfollowPending: false,
  sendMessageSubmitting: false,
  changeObligationReporterSubmitting: false,
  changeReporterModalOpen: false,
  reporters: [],
  reportersLoading: false,
  reportersError: false,
};

const slice = createSlice({
  name: 'submissionDetail',
  initialState: initialState,
  reducers: {
    resetSubmissionDetail: () => initialState,
    toggleFileUploader: (state, { payload }) => {
      state.fileUploaderOpen = payload;
    },
    toggleChangeReporterModal: state => {
      state.changeReporterModalOpen = !state.changeReporterModalOpen;
    },
    toggleAddFollowersModal: state => {
      state.addFollowersModalOpen = !state.addFollowersModalOpen;
    },
  },
  extraReducers: {
    [getSubmission.fulfilled]: (state, { payload }) => {
      state.submissionDetail = {
        ...payload,
        obligation: {
          ...(payload?.obligation ?? {}),
          requiredRowDtos: flattenFileStructure(
            setSequenceNumbers(payload?.obligation?.requiredRowDtos ?? [])
          ),
        },
      };
      state.submissionDetailLoading = false;
      state.submissionDetailError = false;
    },
    [getSubmission.pending]: state => {
      state.submissionDetail = {};
      state.submissionDetailLoading = true;
      state.submissionDetailError = false;
    },
    [getSubmission.rejected]: state => {
      state.submissionDetail = {};
      state.submissionDetailLoading = false;
      state.submissionDetailError = true;
    },
    [uploadFileDetailView.fulfilled]: state => {
      state.fileUploading = false;
    },
    [uploadFileDetailView.pending]: state => {
      state.fileUploading = true;
    },
    [uploadFileDetailView.rejected]: state => {
      state.fileUploading = false;
    },
    [followSubmission.fulfilled]: state => {
      state.submissionDetail.obligation.following = true;
      state.submissionFollowPending = false;
    },
    [followSubmission.pending]: state => {
      state.submissionFollowPending = true;
    },
    [followSubmission.rejected]: state => {
      state.submissionFollowPending = false;
    },
    [unfollowSubmission.fulfilled]: state => {
      state.submissionDetail.obligation.following = false;
      state.submissionUnfollowPending = false;
    },
    [unfollowSubmission.pending]: state => {
      state.submissionUnfollowPending = true;
    },
    [unfollowSubmission.rejected]: state => {
      state.submissionUnfollowPending = false;
    },
    [sendMessage.fulfilled]: state => {
      state.sendMessageSubmitting = false;
    },
    [sendMessage.pending]: state => {
      state.sendMessageSubmitting = true;
    },
    [sendMessage.rejected]: state => {
      state.sendMessageSubmitting = false;
    },
    [changeObligationReporter.fulfilled]: state => {
      state.changeObligationReporterSubmitting = false;
    },
    [changeObligationReporter.pending]: state => {
      state.changeObligationReporterSubmitting = true;
    },
    [changeObligationReporter.rejected]: state => {
      state.changeObligationReporterSubmitting = false;
    },
    [getReporters.fulfilled]: (state, { payload }) => {
      const obligationReporterId = state.obligation?.reporter?.id;
      state.reporters = payload
          .map(entry => entry.publicUserDto)
          .filter(user => user.id !== obligationReporterId);
      state.reportersLoading = false;
      state.reportersError = false;
    },
    [getReporters.pending]: state => {
      state.reportersLoading = true;
      state.reportersError = false;
    },
    [getReporters.rejected]: state => {
      state.reportersLoading = false;
      state.reportersError = true;
    },
    [addFollowers.pending]: (state) => {
      state.followersLoading = true;
      state.followersError = null;
    },
    [addFollowers.fulfilled]: (state, action) => {
      state.followersLoading = false;
      state.followersError = null;
      state.followers = action.payload;
    },
    [addFollowers.rejected]: (state, action) => {
      state.followersLoading = false;
      state.followersError = action.payload || 'Failed to add followers';
    },
  },
});

export const {
  resetSubmissionDetail,
  toggleFileUploader,
  toggleChangeReporterModal,
  toggleAddFollowersModal
} = slice.actions;

export default slice.reducer;
